/* ==========================================================================
   LumiZone — Nuit minérale · scripts du thème (sans dépendance)
   ========================================================================== */
(() => {
  'use strict';

  const theme = window.theme || {};
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };

  /* ---------- Format des prix ---------- */
  function formatMoney(cents, format = theme.moneyFormat || '{{amount_with_comma_separator}} €') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const fmt = (n, precision = 2, thousands = ',', decimal = '.') => {
      const num = (n / 100).toFixed(precision);
      const parts = num.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts[1] && precision > 0 ? whole + decimal + parts[1] : whole;
    };
    const match = format.match(placeholder);
    if (!match) return fmt(cents);
    let value;
    switch (match[1]) {
      case 'amount': value = fmt(cents, 2); break;
      case 'amount_no_decimals': value = fmt(cents, 0); break;
      case 'amount_with_comma_separator': value = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = fmt(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = fmt(cents, 0, ' ', ','); break;
      case 'amount_with_apostrophe_separator': value = fmt(cents, 2, "'", '.'); break;
      default: value = fmt(cents, 2, '.', ',');
    }
    return format.replace(placeholder, value);
  }

  /* ---------- Toast ---------- */
  let toastTimer;
  function toast(message) {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 2800);
  }

  /* ---------- Apparitions au défilement ---------- */
  const revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 })
    : null;

  function initReveal(ctx = document) {
    const items = $$('[data-reveal], .split-line', ctx);
    if (!revealObserver || reduceMotion || !document.body.classList.contains('has-motion')) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    items.forEach((el) => revealObserver.observe(el));
  }

  /* ---------- Verrouillage du défilement + piège de focus ---------- */
  let openLayer = null;
  let lastFocus = null;

  function focusables(el) {
    return $$('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', el)
      .filter((n) => n.offsetParent !== null || n === document.activeElement);
  }

  function openPanel(panel, opener) {
    if (!panel) return;
    if (openLayer && openLayer !== panel) closePanel(openLayer, false);
    lastFocus = opener || document.activeElement;
    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-locked');
    openLayer = panel;
    const first = panel.querySelector('[data-autofocus]') || focusables(panel)[0];
    setTimeout(() => first && first.focus({ preventScroll: true }), 80);
  }

  function closePanel(panel = openLayer, restoreFocus = true) {
    if (!panel) return;
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('is-locked');
    if (openLayer === panel) openLayer = null;
    if (restoreFocus && lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
  }

  document.addEventListener('keydown', (e) => {
    if (!openLayer) return;
    if (e.key === 'Escape') { e.preventDefault(); closePanel(); return; }
    if (e.key === 'Tab') {
      const nodes = focusables(openLayer);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open]');
    if (opener) {
      e.preventDefault();
      const panel = document.getElementById(opener.dataset.open);
      openPanel(panel, opener);
      if (opener.dataset.open === 'CartDrawer') loadUpsell();
      return;
    }
    if (e.target.closest('[data-close]')) {
      e.preventDefault();
      closePanel(e.target.closest('.is-open') || openLayer);
    }
  });

  /* ---------- En-tête : état au défilement ---------- */
  function initHeader() {
    const wrapper = $('[data-header]');
    if (!wrapper) return;
    let lastY = window.scrollY;
    const hideOnScroll = wrapper.dataset.hideOnScroll === 'true';
    const onScroll = () => {
      const y = window.scrollY;
      wrapper.classList.toggle('is-scrolled', y > 24);
      if (hideOnScroll && !openLayer) {
        wrapper.classList.toggle('is-hidden', y > lastY && y > 400 && !wrapper.matches(':focus-within'));
      }
      lastY = y;
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Héros : lumière qui suit le pointeur + parallaxe ---------- */
  function initHero(ctx = document) {
    $$('[data-glow]', ctx).forEach((hero) => {
      if (reduceMotion || window.matchMedia('(hover: none)').matches) return;
      const product = $('.hero__product', hero);
      let frame;
      hero.addEventListener('pointermove', (e) => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const r = hero.getBoundingClientRect();
          const x = (e.clientX - r.left) / r.width;
          const y = (e.clientY - r.top) / r.height;
          hero.style.setProperty('--mx', `${(x * 100).toFixed(1)}%`);
          hero.style.setProperty('--my', `${(y * 100).toFixed(1)}%`);
          if (product) {
            product.style.setProperty('--px', `${((x - 0.5) * -18).toFixed(1)}px`);
            product.style.setProperty('--py', `${((y - 0.5) * -18).toFixed(1)}px`);
          }
        });
      });
    });
  }

  function initParallax() {
    const items = $$('[data-parallax], .split__media > img');
    if (!items.length || reduceMotion) return;
    let ticking = false;
    const update = () => {
      const vh = window.innerHeight;
      items.forEach((el) => {
        const r = el.parentElement.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;
        const progress = (r.top + r.height / 2 - vh / 2) / vh;
        el.style.setProperty('--parallax', `${(progress * -60).toFixed(1)}px`);
      });
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ---------- Panier ---------- */
  const cartSections = () => {
    const ids = ['cart-drawer', 'cart-icon-bubble'];
    if ($('[data-cart-page]')) ids.push($('[data-cart-page]').dataset.sectionId);
    return ids;
  };

  function renderCartSections(sections) {
    if (!sections) return;
    const parser = new DOMParser();
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const doc = parser.parseFromString(html, 'text/html');
      if (id === 'cart-drawer') {
        const fresh = doc.querySelector('[data-cart-drawer-inner]');
        const current = $('[data-cart-drawer-inner]');
        if (fresh && current) current.replaceWith(fresh);
      } else if (id === 'cart-icon-bubble') {
        const fresh = doc.querySelector('[data-cart-count]');
        $$('[data-cart-count]').forEach((el) => {
          if (!fresh) return;
          const changed = el.textContent !== fresh.textContent;
          el.textContent = fresh.textContent;
          el.dataset.count = fresh.dataset.count;
          if (changed) { el.classList.remove('is-bump'); void el.offsetWidth; el.classList.add('is-bump'); }
        });
      } else {
        const fresh = doc.querySelector('[data-cart-page]');
        const current = $('[data-cart-page]');
        if (fresh && current) current.replaceWith(fresh);
      }
    });
  }

  async function cartRequest(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: cartSections(), sections_url: window.location.pathname })
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.error);
    return data;
  }

  async function addToCart(items, button) {
    if (button) button.classList.add('is-loading');
    try {
      const data = await cartRequest(`${theme.routes.cartAdd}.js`, { items });
      renderCartSections(data.sections);
      if (openLayer && openLayer.id === 'QuickView') closePanel(openLayer, false);
      openPanel($('#CartDrawer'), button);
      upsellLoadedFor = null;
      loadUpsell();
    } catch (err) {
      toast(err.message || theme.strings.error);
    } finally {
      if (button) button.classList.remove('is-loading');
    }
  }

  async function changeLine(line, quantity) {
    const row = $(`[data-line="${line}"]`);
    if (row) row.classList.add('is-loading');
    try {
      const data = await cartRequest(`${theme.routes.cartChange}.js`, { line, quantity });
      renderCartSections(data.sections);
      upsellLoadedFor = null;
      loadUpsell();
    } catch (err) {
      toast(err.message || theme.strings.error);
      if (row) row.classList.remove('is-loading');
    }
  }

  // Formulaires produit (fiche, aperçu rapide, barre collante)
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const fd = new FormData(form);
    const id = fd.get('id');
    if (!id) return;
    const button = e.submitter || $('[type="submit"]', form);
    addToCart([{ id: Number(id), quantity: Number(fd.get('quantity') || 1) }], button);
  });

  document.addEventListener('click', (e) => {
    const quickAdd = e.target.closest('[data-quick-add]');
    if (quickAdd) {
      e.preventDefault();
      addToCart([{ id: Number(quickAdd.dataset.quickAdd), quantity: 1 }], quickAdd);
      return;
    }
    const remove = e.target.closest('[data-remove-line]');
    if (remove) {
      e.preventDefault();
      changeLine(Number(remove.dataset.removeLine), 0);
      return;
    }
    const step = e.target.closest('[data-qty-step]');
    if (step) {
      const wrap = step.closest('.qty');
      const input = $('input', wrap);
      const min = Number(input.min || 0);
      const next = Math.max(min, Number(input.value || 0) + Number(step.dataset.qtyStep));
      input.value = next;
      if (input.dataset.line) changeLine(Number(input.dataset.line), next);
      else input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  document.addEventListener('change', (e) => {
    const input = e.target.closest('input[data-line]');
    if (input) changeLine(Number(input.dataset.line), Math.max(0, Number(input.value || 0)));
  });

  // Suggestion de produit complémentaire dans le panier
  let upsellLoadedFor = null;
  async function loadUpsell() {
    if (!theme.settings.cartUpsell) return;
    const holder = $('[data-upsell]');
    if (!holder) return;
    const productId = holder.dataset.productId;
    if (!productId || upsellLoadedFor === productId) return;
    upsellLoadedFor = productId;
    const inCart = (holder.dataset.inCart || '').split(',').filter(Boolean);
    const fetchFor = async (intent) => {
      const url = `${theme.routes.recommendations}?product_id=${productId}&limit=6&intent=${intent}&section_id=cart-upsell`;
      const res = await fetch(url);
      if (!res.ok) return [];
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      return $$('[data-upsell-item]', doc).filter((n) => !inCart.includes(n.dataset.productId));
    };
    try {
      let items = await fetchFor('complementary');
      if (!items.length) items = await fetchFor('related');
      const list = $('[data-upsell-list]', holder);
      if (!items.length || !list) { holder.hidden = true; return; }
      list.innerHTML = '';
      items.slice(0, 2).forEach((n) => list.appendChild(n));
      holder.hidden = false;
    } catch (_) {
      holder.hidden = true;
    }
  }

  /* ---------- Sélecteur de variantes ---------- */
  class VariantPicker {
    constructor(root) {
      this.root = root;
      const json = $('[data-product-json]', root);
      if (!json) return;
      this.product = JSON.parse(json.textContent);
      this.form = $('form[data-product-form]', root);
      this.idInput = this.form && $('input[name="id"]', this.form);
      this.updateUrl = root.dataset.updateUrl === 'true';
      root.addEventListener('change', (e) => {
        const input = e.target.closest('[data-option-input]');
        if (!input) return;
        const group = input.closest('[data-option]');
        this.onChange(true, $$('[data-option]', this.root).indexOf(group));
      });
      this.onChange(false);
    }

    selectedOptions() {
      return $$('[data-option]', this.root).map((group) => {
        const checked = $('input:checked', group);
        const select = $('select', group);
        return checked ? checked.value : select ? select.value : null;
      });
    }

    selectOptions(values) {
      $$('[data-option]', this.root).forEach((group, i) => {
        const input = $$('input[data-option-input]', group).find((n) => n.value === values[i]);
        if (input) input.checked = true;
        const select = $('select', group);
        if (select) select.value = values[i];
      });
    }

    onChange(userAction = true, changedIndex = -1) {
      let options = this.selectedOptions();
      const hasPicker = $$('[data-option]', this.root).length > 0;
      const exact = (opts) => this.product.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      let variant = hasPicker
        ? exact(options)
        : this.product.variants.find((v) => this.idInput && String(v.id) === String(this.idInput.value)) || this.product.variants[0];
      // Combinaison inexistante : on garde la valeur choisie et on ajuste les autres options
      if (hasPicker && (!variant || !variant.available) && changedIndex > -1) {
        const value = options[changedIndex];
        const pool = this.product.variants.filter((v) => v.options[changedIndex] === value);
        const score = (v) => v.options.filter((o, i) => o === options[i]).length + (v.available ? 100 : 0);
        const best = pool.sort((a, b) => score(b) - score(a))[0];
        if (best) {
          this.selectOptions(best.options);
          options = best.options.slice();
          variant = best;
        }
      }
      this.variant = variant;
      this.updateAvailability();
      $$('[data-option]', this.root).forEach((group, i) => {
        const label = $('[data-option-selected]', group);
        if (label) label.textContent = options[i];
      });
      if (!variant) {
        this.setButton(false, theme.strings.unavailable);
        return;
      }
      if (this.idInput) this.idInput.value = variant.id;
      this.setButton(variant.available, variant.available ? theme.strings.addToCart : theme.strings.soldOut);
      this.renderPrice(variant);
      if (userAction && variant.featured_media) this.root.dispatchEvent(new CustomEvent('variant:media', { bubbles: true, detail: { mediaId: variant.featured_media.id } }));
      if (userAction && this.updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant, root: this.root } }));
    }

    updateAvailability() {
      // Barre les valeurs qu'aucune variante disponible ne propose
      $$('[data-option]', this.root).forEach((group, index) => {
        $$('input[data-option-input]', group).forEach((input) => {
          const any = this.product.variants.some((v) => v.available && v.options[index] === input.value);
          input.classList.toggle('is-unavailable', !any);
        });
      });
    }

    setButton(enabled, label) {
      $$('[data-add-button]', this.root).forEach((btn) => {
        btn.disabled = !enabled;
        const text = $('[data-add-label]', btn);
        if (text) text.textContent = label;
      });
    }

    renderPrice(variant) {
      const target = $('[data-price-holder]', this.root);
      if (!target) return;
      const sale = variant.compare_at_price && variant.compare_at_price > variant.price;
      const saving = sale ? Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100) : 0;
      target.innerHTML = `
        <div class="price price--large${sale ? ' price--sale' : ''}">
          <span class="price__current">${formatMoney(variant.price)}</span>
          ${sale ? `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>` : ''}
        </div>
        ${sale ? `<span class="badge badge--sale">−${saving}%</span>` : ''}`;
    }
  }

  /* ---------- Galerie produit ---------- */
  function initGallery(root) {
    const track = $('[data-gallery-track]', root);
    if (!track) return;
    const dots = $$('[data-gallery-dot]', root);
    const items = $$('[data-media-id]', track);

    if ('IntersectionObserver' in window && dots.length) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
            const i = items.indexOf(entry.target);
            dots.forEach((d, j) => d.classList.toggle('is-active', i === j));
          }
        });
      }, { root: track, threshold: [0.6] });
      items.forEach((item) => io.observe(item));
    }

    // Zoom au survol (ordinateur)
    if (window.matchMedia('(hover: hover)').matches) {
      $$('.gallery-product__media[data-zoom]', track).forEach((media) => {
        const img = $('img', media);
        if (!img) return;
        media.addEventListener('click', () => {
          media.classList.toggle('is-zoomed');
          if (!media.classList.contains('is-zoomed')) img.style.transformOrigin = '';
        });
        media.addEventListener('pointermove', (e) => {
          if (!media.classList.contains('is-zoomed')) return;
          const r = media.getBoundingClientRect();
          img.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
        });
        media.addEventListener('pointerleave', () => media.classList.remove('is-zoomed'));
      });
    }

    root.addEventListener('variant:media', (e) => {
      const target = $(`[data-media-id="${e.detail.mediaId}"]`, track);
      if (!target) return;
      if (window.matchMedia('(min-width: 990px)').matches) {
        // Sur ordinateur, la photo de la variante passe en tête de galerie
        if (track.firstElementChild !== target) {
          track.prepend(target);
        }
        if (target.getBoundingClientRect().top < 0) target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      } else {
        track.scrollTo({ left: target.offsetLeft - track.offsetLeft - parseFloat(getComputedStyle(track).paddingLeft || 0), behavior: reduceMotion ? 'auto' : 'smooth' });
      }
    });
  }

  /* ---------- Barre d'achat collante ---------- */
  function initStickyBuy(root) {
    const bar = $('[data-sticky-buy]');
    const anchor = $('[data-add-button]', root);
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    const footer = $('.footer');
    let anchorVisible = true;
    let footerVisible = false;
    const update = () => bar.classList.toggle('is-visible', !anchorVisible && !footerVisible && anchor.getBoundingClientRect().top < 0);
    new IntersectionObserver(([entry]) => { anchorVisible = entry.isIntersecting; update(); }).observe(anchor);
    if (footer) new IntersectionObserver(([entry]) => { footerVisible = entry.isIntersecting; update(); }).observe(footer);

    const select = $('select[data-sticky-variant]', bar);
    const variantLabel = $('[data-sticky-variant-title]', bar);
    const price = $('[data-sticky-price]', bar);
    const button = $('[data-sticky-submit]', bar);
    const idInput = $('input[name="id"]', bar);

    document.addEventListener('variant:change', (e) => {
      if (e.detail.root !== root) return;
      const v = e.detail.variant;
      if (idInput) idInput.value = v.id;
      if (select) select.value = v.id;
      if (variantLabel) variantLabel.textContent = v.title === 'Default Title' ? '' : v.title;
      if (price) price.textContent = formatMoney(v.price);
      if (button) {
        button.disabled = !v.available;
        const label = $('[data-add-label]', button);
        if (label) label.textContent = v.available ? theme.strings.addToCart : theme.strings.soldOut;
      }
    });

    if (select) {
      select.addEventListener('change', () => {
        // Répercute le choix sur le sélecteur principal
        const json = JSON.parse($('[data-product-json]', root).textContent);
        const v = json.variants.find((x) => String(x.id) === select.value);
        if (!v) return;
        $$('[data-option]', root).forEach((group, i) => {
          const input = $$('input[data-option-input]', group).find((n) => n.value === v.options[i]);
          if (input) input.checked = true;
        });
        root.dispatchEvent(new Event('change', { bubbles: true }));
        const picker = $('[data-option-input]', root);
        if (picker) picker.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }

  /* ---------- Initialisation d'une fiche produit (page ou aperçu rapide) ---------- */
  function initProduct(root) {
    if (!root || root.dataset.ready) return;
    root.dataset.ready = 'true';
    new VariantPicker(root);
    initGallery(root);
    if (root.dataset.sticky === 'true') initStickyBuy(root);

    // Galerie miniature de l'aperçu rapide
    $$('[data-qv-thumb]', root).forEach((btn) => {
      btn.addEventListener('click', () => {
        const main = $('[data-qv-main] img', root);
        if (!main) return;
        main.src = btn.dataset.src;
        main.srcset = btn.dataset.srcset || '';
        $$('[data-qv-thumb]', root).forEach((b) => b.classList.toggle('is-active', b === btn));
      });
    });
    root.addEventListener('variant:media', (e) => {
      const btn = $(`[data-qv-thumb][data-media="${e.detail.mediaId}"]`, root);
      if (btn) btn.click();
    });

    // Partage
    const share = $('[data-share]', root);
    if (share) {
      share.addEventListener('click', async () => {
        const data = { title: document.title, url: share.dataset.url || window.location.href };
        try {
          if (navigator.share) await navigator.share(data);
          else { await navigator.clipboard.writeText(data.url); toast(share.dataset.copied); }
        } catch (_) { /* partage annulé */ }
      });
    }
  }

  /* ---------- Aperçu rapide ---------- */
  document.addEventListener('click', async (e) => {
    const trigger = e.target.closest('[data-quick-view]');
    if (!trigger) return;
    e.preventDefault();
    const modal = $('#QuickView');
    const body = $('[data-quick-view-body]', modal);
    if (!modal || !body) { window.location.href = trigger.dataset.quickView; return; }
    body.innerHTML = '<div class="modal__loading"></div>';
    openPanel(modal, trigger);
    try {
      const url = trigger.dataset.quickView.split('?')[0];
      const res = await fetch(`${url}?view=quick`);
      if (!res.ok) throw new Error();
      body.innerHTML = await res.text();
      const root = $('[data-product-root]', body);
      initProduct(root);
      initDeliveryEstimate(body);
      const close = $('[data-close]', modal);
      if (close) close.focus({ preventScroll: true });
    } catch (_) {
      window.location.href = trigger.dataset.quickView;
    }
  });

  /* ---------- Recherche instantanée ---------- */
  function initPredictiveSearch() {
    const modal = $('#SearchModal');
    if (!modal) return;
    const input = $('input[type="search"]', modal);
    const results = $('[data-search-results]', modal);
    const defaults = results ? results.innerHTML : '';
    let controller;

    const search = debounce(async () => {
      const q = input.value.trim();
      if (!q) { results.innerHTML = defaults; return; }
      if (controller) controller.abort();
      controller = new AbortController();
      try {
        const params = new URLSearchParams({
          q,
          section_id: 'predictive-search',
          'resources[type]': 'product,collection,query,page',
          'resources[limit]': '8',
          'resources[limit_scope]': 'each',
          'resources[options][fields]': 'title,product_type,variants.title,vendor,tag'
        });
        const res = await fetch(`${theme.routes.predictiveSearch}?${params}`, { signal: controller.signal });
        if (!res.ok) throw new Error();
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = $('[data-predictive]', doc);
        results.innerHTML = fresh ? fresh.outerHTML : defaults;
        initReveal(results);
      } catch (err) {
        if (err.name !== 'AbortError') results.innerHTML = defaults;
      }
    }, 220);

    input.addEventListener('input', search);
  }

  /* ---------- Filtres de collection ---------- */
  function initFacets() {
    const root = $('[data-facets-root]');
    if (!root) return;
    const sectionId = root.dataset.sectionId;

    const render = async (url, push = true) => {
      const grid = $('[data-collection-grid]');
      if (grid) grid.classList.add('is-loading');
      try {
        const u = new URL(url, window.location.origin);
        u.searchParams.set('section_id', sectionId);
        const res = await fetch(u.toString());
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        ['[data-collection-grid]', '[data-facets-form-body]', '[data-active-filters]', '[data-product-count]', '[data-filter-count]'].forEach((sel) => {
          const fresh = $(sel, doc);
          $$(sel).forEach((cur) => { if (fresh) cur.innerHTML = fresh.innerHTML; });
        });
        u.searchParams.delete('section_id');
        if (push) window.history.pushState({ facets: true }, '', u.pathname + u.search);
        if (push && u.searchParams.has('page')) root.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        initReveal($('[data-collection-grid]') || document);
      } catch (_) {
        window.location.href = url;
      } finally {
        const g = $('[data-collection-grid]');
        if (g) g.classList.remove('is-loading');
      }
    };

    const formUrl = (form) => {
      const params = new URLSearchParams(new FormData(form));
      // Retire les bornes de prix vides
      ['filter.v.price.gte', 'filter.v.price.lte'].forEach((k) => { if (params.get(k) === '') params.delete(k); });
      const sort = $('[data-sort]');
      if (sort && sort.value) params.set('sort_by', sort.value);
      return `${window.location.pathname}?${params.toString()}`;
    };

    const onChange = debounce((form) => render(formUrl(form)), 350);

    document.addEventListener('change', (e) => {
      const form = e.target.closest('[data-facets-form]');
      if (form) { onChange(form); return; }
      if (e.target.matches('[data-sort]')) {
        const f = $('[data-facets-form]');
        if (f) render(formUrl(f));
        else {
          const u = new URL(window.location.href);
          u.searchParams.set('sort_by', e.target.value);
          render(u.toString());
        }
      }
    });
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('[data-facets-form]');
      if (!form) return;
      e.preventDefault();
      render(formUrl(form));
      closePanel();
    });
    document.addEventListener('click', (e) => {
      const link = e.target.closest('[data-facet-link]');
      if (!link) return;
      e.preventDefault();
      render(link.href);
    });
    window.addEventListener('popstate', (e) => {
      if (e.state && e.state.facets) render(window.location.href, false);
    });
  }

  /* ---------- Recommandations produit (fiche) ---------- */
  function initRecommendations() {
    $$('[data-recommendations]').forEach(async (el) => {
      try {
        const res = await fetch(el.dataset.url);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = $('[data-recommendations]', doc);
        if (fresh && fresh.innerHTML.trim()) {
          el.innerHTML = fresh.innerHTML;
          initReveal(el);
        } else {
          el.closest('.section') && (el.closest('.section').hidden = true);
        }
      } catch (_) { /* silencieux */ }
    });
  }

  /* ---------- Estimation de livraison (jours ouvrés) ---------- */
  function initDeliveryEstimate(ctx = document) {
    $$('[data-delivery-estimate]', ctx).forEach((el) => {
      const addBusinessDays = (date, days) => {
        const d = new Date(date);
        let added = 0;
        while (added < days) {
          d.setDate(d.getDate() + 1);
          const day = d.getDay();
          if (day !== 0 && day !== 6) added += 1;
        }
        return d;
      };
      const lang = document.documentElement.lang || 'fr';
      const fmt = new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'long' });
      const now = new Date();
      const min = fmt.format(addBusinessDays(now, Number(el.dataset.min || 8)));
      const max = fmt.format(addBusinessDays(now, Number(el.dataset.max || 18)));
      const esc = (s) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
      const decode = (s) => { const t = document.createElement('textarea'); t.innerHTML = s; return t.value; };
      const tpl = decode(el.dataset.template || '').replace(/<\/?strong>/g, '');
      el.innerHTML = esc(tpl).replace('[min]', '<strong>' + min + '</strong>').replace('[max]', '<strong>' + max + '</strong>');
    });
  }

  /* ---------- Éditeur de thème Shopify ---------- */
  document.addEventListener('shopify:section:load', (e) => {
    portalLayers(e.target);
    initReveal(e.target);
    initHero(e.target);
    $$('[data-product-root]', e.target).forEach(initProduct);
    initDeliveryEstimate(e.target);
  });

  /* ---------- Panneaux : sortis du contenu pour passer au-dessus de l'en-tête ---------- */
  function portalLayers(ctx = document) {
    const isOverlay = (n) => n && n.classList.contains('overlay');
    $$('main .drawer, main .modal', ctx).forEach((el) => {
      const overlay = isOverlay(el.nextElementSibling) ? el.nextElementSibling : null;
      if (el.id) {
        $$(`body > #${CSS.escape(el.id)}`).forEach((old) => {
          if (old === el) return;
          if (isOverlay(old.nextElementSibling)) old.nextElementSibling.remove();
          old.remove();
        });
      }
      document.body.appendChild(el);
      if (overlay) document.body.appendChild(overlay);
    });
  }

  /* ---------- Démarrage ---------- */
  function boot() {
    portalLayers();
    initReveal();
    initHeader();
    initHero();
    initParallax();
    $$('[data-product-root]').forEach(initProduct);
    initPredictiveSearch();
    initFacets();
    initRecommendations();
    initDeliveryEstimate();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.LumiZone = { formatMoney, openPanel, closePanel, toast, addToCart };
})();
